/**
 * DocumentPreviewModal - gemeinsame, wiederverwendbare Dokumentvorschau.
 *
 * Generalisiert das bestehende Inline-Modal-Muster (bisher pro Seite
 * dupliziert, z.B. VorgangDetailPage.jsx) zu EINER gemeinsamen Komponente --
 * ZEO interaktive Antworten (06.10.2026, Abschnitt 4) verlangt ausdruecklich
 * "KEIN zweiter Dokumentviewer", wenn bereits einer vorhanden ist. Nutzt
 * denselben Ticket-basierten Vorschau-Mechanismus (holePreviewUrl).
 */
import { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { holePreviewUrl } from '@/lib/downloadTicket';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

/** Vorschau-URL (Ticket, inline) + Lade-/Fehlerzustand -- gemeinsam fuer das Modal und die eingebettete Vorschau. */
export function useDokumentVorschau(dokument) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fehler, setFehler] = useState(false);
  const [nichtVerfuegbar, setNichtVerfuegbar] = useState(false);

  useEffect(() => {
    let aktuell = true;
    if (!dokument) { setPreviewUrl(null); return; }
    setFehler(false);
    setNichtVerfuegbar(false);
    const token = localStorage.getItem('olympus_token');
    holePreviewUrl(
      `${API}/api/betreuung/dokumente/${dokument.id}/download-ticket`,
      `${API}/api/betreuung/dokumente/${dokument.id}/download?inline=true`,
      { 'Authorization': `Bearer ${token}` },
    )
      .then((url) => { if (aktuell) setPreviewUrl(url); })
      .catch((e) => { if (aktuell) { setFehler(true); setNichtVerfuegbar(e && e.status === 404); } });
    return () => { aktuell = false; };
  }, [dokument]);
  return { previewUrl, fehler, nichtVerfuegbar };
}

/** Inhalt der Vorschau (Fehler / iframe / Laden) -- unveraendert aus dem Modal, auch eingebettet nutzbar (Neuanlage aus Beschluss, 28.10.2026). */
export function DokumentVorschauInhalt({ dokument, hoehe = 'min-h-[70vh]' }) {
  const { previewUrl, fehler, nichtVerfuegbar } = useDokumentVorschau(dokument);
  if (!dokument) return null;
  return fehler ? (
    <div className={`w-full h-full ${hoehe} flex items-center justify-center text-sm text-slate-500 text-center px-6`} data-testid="document-original-unavailable">
      {nichtVerfuegbar
        ? 'Original nicht verfügbar – die Datei zu diesem Dokument ist im Ablagesystem nicht auffindbar. Inhaltliche Aussagen stützen sich nur auf den gespeicherten Text und können nicht am Original verifiziert werden.'
        : 'Vorschau konnte nicht geladen werden.'}
    </div>
  ) : previewUrl ? (
    <iframe src={previewUrl} className={`w-full h-full ${hoehe} rounded`} title="Dokumenten-Vorschau" data-testid="document-preview-frame" />
  ) : (
    <div className={`w-full h-full ${hoehe} flex items-center justify-center`}>
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );
}

export default function DocumentPreviewModal({ dokument, onClose }) {
  if (!dokument) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <p className="font-semibold text-slate-800 truncate">{dokument.titel || dokument.dateiname || 'Dokument'}</p>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="document-preview-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 min-h-0 p-1">
          <DokumentVorschauInhalt dokument={dokument} />
        </div>
      </div>
    </div>
  );
}
